import React from 'react';
import { TrendingUp, Target, Shield, ArrowRight, BarChart3, Calculator, Clock } from 'lucide-react';
import compoundingImage from '../../assets/coaching-team.jpg';

const CompoundingPlansPage = () => {
  const plans = [
    {
      name: 'Starter Compound',
      minInvestment: '$1,000',
      maxInvestment: '$9,999',
      annualReturn: '15%',
      duration: '12 months',
      compoundingFrequency: 'Monthly',
      features: [
        'Monthly compounding',
        'Flexible withdrawal options',
        'Dedicated account manager',
        'Quarterly performance reports',
        'Risk assessment included'
      ]
    },
    {
      name: 'Growth Compound',
      minInvestment: '$10,000',
      maxInvestment: '$49,999',
      annualReturn: '20%',
      duration: '18 months',
      compoundingFrequency: 'Monthly',
      features: [
        'Enhanced monthly compounding',
        'Priority customer support',
        'Advanced portfolio analytics',
        'Tax optimization strategies',
        'Reinvestment options',
        'Market insights access'
      ]
    },
    {
      name: 'Elite Compound',
      minInvestment: '$50,000',
      maxInvestment: '$250,000+',
      annualReturn: '25%',
      duration: '24 months',
      compoundingFrequency: 'Weekly',
      features: [
        'Weekly compounding advantage',
        'VIP relationship manager',
        'Personalized investment strategy',
        'Exclusive market opportunities',
        'Advanced risk management',
        'Premium research access',
        'Estate planning consultation'
      ]
    }
  ];

  const benefits = [
    {
      icon: Calculator,
      title: 'Exponential Growth',
      description: 'Harness the power of compound interest to accelerate your wealth building through systematic reinvestment of returns.'
    },
    {
      icon: Clock,
      title: 'Time Advantage',
      description: 'The longer your investment period, the more significant the compounding effect becomes on your overall returns.'
    },
    {
      icon: BarChart3,
      title: 'Consistent Performance',
      description: 'Our structured approach to compounding ensures steady and predictable growth patterns over time.'
    },
    {
      icon: Shield,
      title: 'Risk Mitigation',
      description: 'Diversified compounding strategies help reduce volatility while maintaining strong growth potential.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/80 to-navy-800/60"></div>
        <div className="absolute inset-0">
          <img
            src={compoundingImage}
            alt="Compounding Plans"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold font-serif mb-4">Compounding Plans</h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Home / Plans / <span className="text-gold-400">Compounding Plans</span>
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
              Compounding Investment Plans at <span className="text-gold-600">HULA CAPITAL LIMITED</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Unlock the true power of compound interest with our strategic investment plans designed to maximize your long-term wealth accumulation through systematic reinvestment.
            </p>
          </div>

          {/* Benefits Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-navy-900 mb-4">Why Choose Compounding?</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div key={index} className="text-center group">
                    <div className="inline-flex p-4 bg-gradient-to-r from-gold-500 to-gold-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-navy-900 mb-3">
                      {benefit.title}
                    </h4>
                    <p className="text-gray-600">
                      {benefit.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Plans Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-navy-900 mb-4">Our Compounding Plans</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <div key={index} className={`relative bg-white p-8 rounded-2xl shadow-xl border-2 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 ${index === 1 ? 'border-gold-500 scale-105' : 'border-gray-200'}`}>
                  {index === 1 && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gold-500 text-white px-6 py-2 rounded-full text-sm font-bold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <h4 className="text-2xl font-bold text-navy-900 mb-2">{plan.name}</h4>
                    <div className="text-4xl font-bold text-gold-600 mb-2">{plan.annualReturn}</div>
                    <p className="text-gray-600">Annual Return</p>
                  </div>

                  <div className="space-y-4 mb-8">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Investment Range:</span>
                      <span className="font-semibold">{plan.minInvestment} - {plan.maxInvestment}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-semibold">{plan.duration}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Compounding:</span>
                      <span className="font-semibold">{plan.compoundingFrequency}</span>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h5 className="font-bold text-navy-900 mb-4">Plan Features:</h5>
                    <ul className="space-y-2">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center">
                          <Target className="h-4 w-4 text-gold-500 mr-2 flex-shrink-0" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button className={`w-full py-4 rounded-full font-bold transition-colors duration-300 ${index === 1 ? 'bg-gold-500 hover:bg-gold-600 text-white' : 'bg-navy-900 hover:bg-navy-800 text-white'}`}>
                    Choose Plan
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* How It Works Section */}
          <div className="mb-20">
            <div className="bg-gray-50 rounded-2xl p-12">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-navy-900 mb-4">How Compounding Works</h3>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Our compounding strategy reinvests your returns to generate additional earnings, creating a snowball effect that accelerates your wealth accumulation over time.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="bg-gold-500 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">1</div>
                  <h4 className="text-xl font-bold text-navy-900 mb-2">Initial Investment</h4>
                  <p className="text-gray-600">Start with your chosen investment amount in your selected compounding plan.</p>
                </div>
                <div className="text-center">
                  <div className="bg-gold-500 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">2</div>
                  <h4 className="text-xl font-bold text-navy-900 mb-2">Generate Returns</h4>
                  <p className="text-gray-600">Your investment generates returns based on the plan's specified annual rate.</p>
                </div>
                <div className="text-center">
                  <div className="bg-gold-500 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">3</div>
                  <h4 className="text-xl font-bold text-navy-900 mb-2">Automatic Reinvestment</h4>
                  <p className="text-gray-600">Returns are automatically reinvested to compound your earnings exponentially.</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl md:text-4xl font-bold font-serif mb-6">
              Start Your Compounding Journey Today
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Join thousands of investors who are building wealth through the power of compound interest. 
              Choose your plan and watch your money grow exponentially.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="inline-flex items-center justify-center px-8 py-4 bg-gold-500 hover:bg-gold-600 text-white font-bold rounded-full transition-colors duration-300">
                Start Investing
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-navy-900 font-bold rounded-full transition-all duration-300">
                Calculate Returns
                <Calculator className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompoundingPlansPage; 